/**
 * LE PROCÈS-VERBAL D'INVENTAIRE — le document scellé d'une campagne close.
 *
 * « Document scellé avec l'inventaire — réimprimable à l'identique pendant six ans, prêt pour la
 * comptabilité » (maquette BO, écran `inv`).
 *
 * ⚠️ CETTE PAGE NE CALCULE RIEN. Elle affiche ce que la clôture a écrit, tel quel. C'est toute la
 * raison d'être des tables `inventory_report*` : le rapprochement, lui, se recalcule depuis une
 * fenêtre de mouvements qui court jusqu'à « maintenant » — un PV régénéré dans trois ans ne rendrait
 * pas les mêmes nombres, et dépendrait de la version du logiciel qui le réimprime.
 *
 * ⚠️ CE QUI N'EST PAS OFFERT ICI : l'envoi par mail, présent à la maquette. La messagerie sortante
 * n'est pas câblée au back-office (`mail-settings` sert la Console, le store et l'EDI — pas lui). Un
 * bouton qui ouvrirait puis ne partirait pas serait une option offerte non implémentée (§23.0).
 */
import { useCallback, useEffect, useState } from 'react';
import { EmptyState, showToast, Trans, useLatest, useOverlayPanel } from '@orgazone/ui-web';
import { currentCurrency, formatMoney, toInputValue } from '@orgazone/money-format';
import { api, type InventoryReportHeader, type InventoryReportLine } from '../api';
import { exporterCsv } from './export-csv';
import { BlocAVenir } from './a-venir';
import { fieldLabel } from '../vocabulary';
import { t } from '../i18n';
import { readReason } from '../written-reasons';

const TAILLE_PAGE = 200;

/**
 * LES EN-TÊTES DU CSV DU PROCÈS-VERBAL — le mot du MAGASIN pour les colonnes qui sont un champ du
 * catalogue ; les mots du document (« Théorique », « Compté », « Écart », « Motif »…) restent ce
 * qu'ils sont, et la colonne de valeur annonce la DEVISE courante.
 * ⚠️ UNE FONCTION APPELÉE AU CLIC, jamais une constante de module : le mot de la plateforme — et la
 *    devise — s'y figeraient avant la lecture du contexte magasin. EXPORTÉE pour l'aller-retour
 *    export→import, éprouvé par un test : le masque d'import rattache chaque colonne de champ à son
 *    champ.
 */
export function inventoryReportCsvHeaders(): string[] {
  return [
    fieldLabel('gencode'), fieldLabel('articleCode'), fieldLabel('name'), fieldLabel('size'),
    t('Théorique'), t('Théorique au comptage'), t('Compté'), t('Écart'), t('Valeur ({code})', { code: currentCurrency().code }), t('Exclu'), t('Motif'), t('Approximatif'),
  ];
}

const dateHeure = (ms: number): string => new Date(ms).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** ± signé — `null` reste un tiret : une valeur inconnue se DIT, elle ne devient pas 0. */
const signe = (n: number): string => (n > 0 ? `+${n}` : String(n));

function Trait({ d, size = 14, stroke = 2 }: { d: string; size?: number; stroke?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>;
}

/** Les repères du document — relevés de la maquette (`invRpSum`), tous issus du scellement. */
function reperes(h: InventoryReportHeader): { k: string; v: string }[] {
  return [
    { k: t('Références'), v: String(h.linesCount) },
    { k: t('Comptées'), v: String(h.countedCount) },
    { k: t('Jamais bipées'), v: String(h.notCountedCount) },
    { k: t('Exclues'), v: String(h.excludedCount) },
    { k: t('En écart'), v: String(h.varianceCount) },
    { k: t('Remises à zéro'), v: String(h.zeroedCount) },
    { k: t('Solde net'), v: signe(h.deltaQty) },
    { k: t('Écart au PMP ({code})', { code: currentCurrency().code }), v: formatMoney(h.deltaValueCents, { thousandsSep: ' ' }) },
    // ⚠️ « NON CHIFFRÉ » ET NON « 0,00 € » : des procès-verbaux ont été scellés avant que ces montants
    //    existent. Zéro affirmerait « stock sans valeur » ; ces documents ne portaient pas l'information.
    { k: t('Stock avant'), v: h.theoreticalValueCents == null ? t('non chiffré') : formatMoney(h.theoreticalValueCents, { thousandsSep: ' ' }) },
    { k: t('Stock après'), v: h.finalValueCents == null ? t('non chiffré') : formatMoney(h.finalValueCents, { thousandsSep: ' ' }) },
  ];
}

export function ProcesVerbal({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const [header, setHeader] = useState<InventoryReportHeader | null>(null);
  const [rows, setRows] = useState<InventoryReportLine[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [ecartsSeuls, setEcartsSeuls] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LA PAGE : on tourne les pages et on coche « écarts seuls » plus vite que le
  //    serveur ne répond — la page précédente, revenue en dernier, s'afficherait sous le numéro de la suivante, et
  //    l'export, qui prend la page AFFICHÉE, l'écrirait au fichier.
  const latest = useLatest();
  const reload = useCallback(async () => {
    const isLatest = latest();
    try {
      setFailure(null);
      const r = await api.inventoryReport(campaignId, { offset: page * TAILLE_PAGE, limit: TAILLE_PAGE, variancesOnly: ecartsSeuls });
      if (!isLatest()) return;
      setHeader(r.header); setRows(r.rows); setTotal(r.total);
    } catch (e) { if (isLatest()) setFailure(e instanceof Error ? e.message : String(e)); }
  }, [campaignId, page, ecartsSeuls, latest]);
  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => { setPage(0); }, [ecartsSeuls]);

  /**
   * Export du document.
   *
   * ⚠️ IL EXPORTE LA PAGE AFFICHÉE, ET LE DIT. Un export qui tirerait silencieusement les trente mille
   * lignes derrière ferait attendre sans prévenir ; un export qui n'en prendrait que deux cents SANS
   * le dire produirait un fichier incomplet qu'on croirait complet — c'est celui-là qui est dangereux.
   */
  /**
   * PEUT-ON ENVOYER ? — demandé au serveur, une fois, à l'ouverture.
   * ⚠️ `null` tant qu'on ne sait pas : ni bouton, ni explication. Afficher l'un des deux avant la
   * réponse ferait clignoter l'écran entre deux vérités.
   */
  const [envoi, setEnvoi] = useState<{ ready: boolean; why: string } | { ready: null; why: '' }>({ ready: null, why: '' });
  const [destinataire, setDestinataire] = useState('');
  const [mot, setMot] = useState('');
  const [enVol, setEnVol] = useState(false);

  useEffect(() => {
    let vivant = true;
    void api.mailReadiness()
      .then((r) => { if (vivant) setEnvoi(r); })
      // ⚠️ Une lecture ratée n'est pas « pas de transport » : on ne sait RIEN, donc on ne dit rien.
      .catch(() => { /* l'écran reste muet sur ce point */ });
    return () => { vivant = false; };
  }, []);

  /** ENVOYER — et dire ce qui est parti, à qui. */
  const envoyer = useCallback(async () => {
    setEnVol(true);
    try {
      const r = await api.mailInventoryReport(campaignId, destinataire.trim(), mot.trim());
      showToast(t('Rapport envoyé à {to}', { to: r.to }), 'ok', 6000);
      setDestinataire('');
      setMot('');
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 8000); }
    finally { setEnVol(false); }
  }, [campaignId, destinataire, mot]);

// ⚠️ MONTANTS DU CSV : `toInputValue` (money-format) et non « ÷ 100 puis deux décimales ». Il rend
//    la même chaîne pour l'euro, mais il lit l'ÉCHELLE de la devise courante — 0 pour le yen, 3
//    pour les dinars — alors que l'en-tête annonce déjà cette devise. Les deux moitiés de la même
//    colonne ne peuvent pas être en désaccord sur ce point. Et il calcule sur des entiers.
  const exporter = useCallback(() => {
    if (!header) return;
    exporterCsv(
      // ⚠️ LE NOM DU FICHIER NE SE TRADUIT PAS : c'est la convention de tous les exports du
      //    back-office (`clients`, `titres-et-bons`, `client-rgpd`), et un nom qui change avec la
      //    langue du compte rendrait deux fichiers différents pour le même inventaire.
      `pv-inventaire-${header.campaignId}`,
      inventoryReportCsvHeaders(),
      rows.map((r) => [
        r.gencode, r.articleCode, r.name, r.sizeLabel,
        String(r.theoreticalQtyMinor), String(r.theoreticalAtCount),
        r.countedQtyMinor == null ? '' : String(r.countedQtyMinor),
        String(r.deltaQtyMinor),
        toInputValue(r.deltaValueCents),
        r.excluded ? t('oui') : '', readReason(r.excludedReason), r.uncertain ? t('oui') : '',
      ]),
    );
    showToast(t('{n} ligne exportée — la page affichée', { n: rows.length }), 'ok');
  }, [header, rows]);

  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.55)' }} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={t('Rapport d’inventaire')} style={{ position: 'relative', width: 880, maxWidth: '100%', maxHeight: 'calc(100vh - 40px)', background: 'var(--paper)', borderRadius: 'var(--r-md)', boxShadow: '0 24px 70px rgba(10,12,14,.35)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '15px 20px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-h3)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)' }}>{t('Rapport d’inventaire')}</span>
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{campaignId}</span>
          <span style={{ flex: 1 }} />
          {header ? (
            <>
              <button onClick={() => window.print()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>
                <Trait d="M7 17h10v5H7zM7 12H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v3a2 2 0 01-2 2h-2M7 12h10v5H7z" />{t('Imprimer')}
              </button>
              {/* ⚠️ UN VRAI PDF, PAS « ENREGISTRER EN PDF » DEPUIS L'IMPRESSION. Le fichier du navigateur
                  dépend de ses marges et de ses en-têtes, il change d'un poste à l'autre, et surtout le
                  SERVEUR ne peut pas le produire — or c'est lui qui devra l'attacher à un mail. Le
                  document sort des données SCELLÉES : six mois plus tard, il dira les mêmes chiffres. */}
              <a href={api.inventoryReportPdfUrl(campaignId)} download
                style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, textDecoration: 'none' }}>
                <Trait d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M12 18v-6m0 0-2.5 2.5M12 12l2.5 2.5" />PDF
              </a>
              <button onClick={exporter}
                style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-800)', color: '#fff', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>
                <Trait d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />{t('Export Excel')}
              </button>
            </>
          ) : null}
          <button onClick={onClose} aria-label={t('Fermer')} style={{ width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Trait d="M6 6l12 12M18 6L6 18" stroke={2.2} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          {failure ? (
            // ⚠️ Le message RÉEL — sur une campagne non close, il DIT que le document n'existe pas
            //    encore, au lieu d'afficher un rapport vide qu'on prendrait pour un inventaire nul.
            <div style={{ fontSize: '12.5px', color: 'var(--rose-fg)', lineHeight: 1.55 }}>{failure}</div>
          ) : !header ? (
            <div style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture du document…')}</div>
          ) : (
            <>
              <div style={{ fontSize: '12.5px', color: 'var(--ink-600)', marginBottom: 12, lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--ink-900)' }}>{header.label}</strong>
                {' · '}{t('scellé le {date} par {qui}', { date: dateHeure(header.sealedAt), qui: header.sealedBy || '—' })}
                {header.startedAt ? <>{' · '}{t('théorique figé le {v}', { v: dateHeure(header.startedAt) })}</> : ''}
              </div>

              {header.liveDuring ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', marginBottom: 12 }}>
                  <span style={{ display: 'flex', paddingTop: 1 }}><Trait d="M6 2l1.5 3h9L18 2M3 6h18l-2 13H5L3 6zM9 11v5M15 11v5" /></span>
                  {/* ⚠️ C'est la PREMIÈRE question d'un contrôleur, et elle doit figurer sur le document
                      lui-même — pas seulement à l'écran du jour de la validation. */}
                  <span style={{ fontSize: '12.5px', lineHeight: 1.55 }}>
                    <Trans k='<1>Inventaire à chaud.</1> Le magasin a vendu pendant le comptage : les écarts sont calculés contre le théorique ramené au moment où chaque ligne a été comptée.' parts={[(x) => <strong>{x}</strong>]} /></span>
                </div>
              ) : null}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
                {reperes(header).map((r) => (
                  <div key={r.k} style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', padding: '9px 12px', minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.k}</div>
                    <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{r.v}</div>
                  </div>
                ))}
              </div>

              {header.sizeInversions.length > 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--ink-600)', marginBottom: 12, lineHeight: 1.55 }}>
                  <Trans k='<1>{n} inversion de taille relevée</1> — un excédent sur une taille et un manque sur une autre du même modèle : erreur de scan à la vente, pas une perte. {v}' vars={{ n: header.sizeInversions.length, v: header.sizeInversions.slice(0, 5).map((i) => `${i.articleCode} (${i.underSize ?? '?'} → ${i.overSize ?? '?'}, ${i.qtyMinor})`).join(' · ') }} parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} />
                </div>
              ) : null}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12.5px', color: 'var(--ink-700)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={ecartsSeuls} onChange={(e) => setEcartsSeuls(e.target.checked)} />
                  
                  {t('Écarts seulement')}
                </label>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
                  {total === 0 ? t('0 ligne') : t('{v}–{v2} sur {total}', { v: page * TAILLE_PAGE + 1, v2: Math.min((page + 1) * TAILLE_PAGE, total), total })}
                </span>
              </div>

              <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
                {rows.length === 0 ? (
                  <EmptyState icon="box" title={ecartsSeuls ? t('Aucun écart') : t('Aucune ligne')} note={ecartsSeuls ? t('Tout ce qui a été compté correspondait au théorique — c’est exactement l’objectif.') : t('Ce document ne porte aucune ligne.')} compact />
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                      <thead>
                        <tr style={{ background: 'var(--ink-50)' }}>
                          {[t('Article'), t('Théorique'), t('Compté'), t('Écart'), t('Valeur PMP')].map((h, i) => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: i === 0 ? 'left' : 'right', fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.gencode} style={{ borderTop: '1px solid var(--ink-100)', opacity: r.excluded ? 0.65 : 1 }}>
                            <td style={{ padding: '6px 12px' }}>
                              <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)' }}>{r.name}{r.sizeLabel ? ` · ${r.sizeLabel}` : ''}</span>
                              <span style={{ display: 'block', fontSize: 'var(--t-micro)', color: 'var(--ink-400)', fontFamily: 'var(--font-mono)' }}>{r.gencode}</span>
                              {r.excluded ? (
                                <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--teal-800)', fontWeight: 700 }}>{t('Exclu —')} {r.excludedReason ? readReason(r.excludedReason) : t('sans motif')}</span>
                              ) : null}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>
                              {r.theoreticalQtyMinor}
                              {r.theoreticalAtCount !== r.theoreticalQtyMinor ? (
                                <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--blue-fg)', fontWeight: 700 }}>→ {r.theoreticalAtCount}</span>
                              ) : null}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--ink-900)' }}>
                              {r.countedQtyMinor == null ? '—' : r.countedQtyMinor}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: r.deltaQtyMinor !== 0 ? 700 : 400, color: r.deltaQtyMinor < 0 ? 'var(--rose-fg)' : r.deltaQtyMinor > 0 ? 'var(--teal-800)' : 'var(--ink-500)' }}>
                              {r.deltaQtyMinor === 0 ? '0' : signe(r.deltaQtyMinor)}
                              {r.uncertain ? <span title={t('Compté dans plusieurs zones avec du mouvement entre les deux — correction approximative.')} style={{ marginLeft: 4, color: 'var(--amber-fg)' }}>≈</span> : null}
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-700)', whiteSpace: 'nowrap' }}>
                              {r.unitCostFine === 0
                                ? <span title={t('Aucun coût connu — l’écart de cette ligne n’a pas pu être valorisé.')} style={{ color: 'var(--ink-400)' }}>{t('non valorisé')}</span>
                                : formatMoney(r.deltaValueCents, { thousandsSep: ' ' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {total > TAILLE_PAGE ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
                    <span style={{ flex: 1 }} />
                    <button disabled={page === 0} onClick={() => setPage((n) => Math.max(0, n - 1))}
                      style={{ height: 30, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: page === 0 ? 'var(--ink-300)' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: page === 0 ? 'default' : 'pointer' }}>{t('Précédentes')}</button>
                    <button disabled={(page + 1) * TAILLE_PAGE >= total} onClick={() => setPage((n) => n + 1)}
                      style={{ height: 30, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: (page + 1) * TAILLE_PAGE >= total ? 'var(--ink-300)' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: (page + 1) * TAILLE_PAGE >= total ? 'default' : 'pointer' }}>{t('Suivantes')}</button>
                  </div>
                ) : null}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 8, marginTop: 12 }}>
                {/* ⚠️ LE BOUTON N'EXISTE QUE SI L'ENVOI EST POSSIBLE (§23.0). Un bouton qui ouvre puis
                    ne part pas est pire que son absence : l'opérateur croit le rapport envoyé, et
                    personne ne le détrompe. Quand le serveur n'a pas de transport, on dit POURQUOI —
                    c'est une affaire de déploiement, pas une fonction manquante. */}
                {envoi.ready === true ? (
                  <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: '13px 15px', background: 'var(--paper)' }}>
                    <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 4 }}>{t('Envoyer par mail')}</div>
                    <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55, marginBottom: 10 }}>
                      <Trans k='Le procès-verbal part en <1>pièce jointe</1> — le destinataire n’a pas besoin d’un compte au back-office.' parts={[(x) => <strong>{x}</strong>]} /></div>
                    <input value={destinataire} onChange={(e) => setDestinataire(e.target.value)}
                      placeholder={t('comptable@cabinet.fr')} inputMode="email"
                      style={{ width: '100%', boxSizing: 'border-box', height: 36, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-body)', outline: 'none' }} />
                    <input value={mot} onChange={(e) => setMot(e.target.value)}
                      placeholder={t('Un mot d’accompagnement (facultatif)')}
                      style={{ width: '100%', boxSizing: 'border-box', height: 36, padding: '0 11px', marginTop: 7, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-body)', outline: 'none' }} />
                    <button onClick={() => void envoyer()} disabled={enVol || destinataire.trim() === ''}
                      style={{ marginTop: 9, height: 34, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-800)', color: '#fff', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: enVol || !destinataire.trim() ? 'default' : 'pointer', opacity: enVol || !destinataire.trim() ? 0.5 : 1 }}>
                      {enVol ? t('Envoi…') : t('Envoyer le rapport')}
                    </button>
                  </div>
                ) : envoi.ready === false ? (
                  <BlocAVenir titre={t('Envoyer par mail')}>{envoi.why}</BlocAVenir>
                ) : null}
                <BlocAVenir titre={t('Export comptable')}>
                  
                  {t('La valeur de stock de clôture, la démarque et la dépréciation, au format attendu par le cabinet. L’export ci-dessus donne les LIGNES de l’inventaire ; celui-là donnera les ÉCRITURES — et il attend le plan comptable, qui n’existe pas encore.')}
                </BlocAVenir>
              </div>
              <div style={{ marginTop: 10, fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.55 }}>
                
                {t('Document scellé avec l’inventaire — réimprimable à l’identique, prêt pour la comptabilité.')}
                {/* ⚠️ On ne promet pas « six ans » comme la maquette : la rétention des bases magasin n'est
                    pas encore réglée, et une durée annoncée qu'on ne garantit pas est une promesse en
                    l'air. Ce qu'on affirme ici est vrai : le document ne bouge plus. */}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
